import React, { useState, useEffect } from "react";
import { FaDownload, FaCheckCircle, FaTimes, FaUpload, FaBullhorn, FaMapMarkerAlt, FaEye, FaHistory, FaSearch, FaInfoCircle, FaCheck, FaGlobe, FaChevronRight } from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import "react-toastify/dist/ReactToastify.css";
import InfluenerRaiseVoiceTabs from "./InfluenerRaiseVoiceTabs";

const InfluenerRaiseAcceptedVoice = () => {
  const [ads, setAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(ads.length / rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    adId: "",
    videoLink: "",
    platformName: "",
    adTiming: "",
  });

  const [showSubmitSpinner, setShowSubmitSpinner] = useState(false);
  const [downloadAdId, setDownloadAdId] = useState(null);

  useEffect(() => {
    const fetchAcceptedAds = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/reporter/get/accepted/ryv/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const adsData = Array.isArray(response.data.ads)
          ? response.data.ads
          : Array.isArray(response.data.data)
            ? response.data.data
            : Array.isArray(response.data.advertisements)
              ? response.data.advertisements
              : [];
        setAds(adsData);
        setError(null);
      } catch (err) {
        setError("Failed to load accepted ads. Please try again later.");
        toast.error("Failed to load accepted ads");
      } finally {
        setLoading(false);
      }
    };
    fetchAcceptedAds();
  }, []);

  useEffect(() => {
    const fetchAdPricing = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/ad/pricing`
        );
        setPlatforms(res.data.data.plateforms || []);
      } catch (err) {
        console.error("Error fetching ad pricing:", err);
      }
    };
    fetchAdPricing();
  }, []);

  const handleDownload = async (ad) => {
    try {
      setDownloadAdId(ad._id);
      const url = ad.mediaType?.toLowerCase() === "video" ? ad.videoUrl : ad.imageUrl;
      let ext = "";
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
        ext = match ? match[1] : (ad.mediaType?.toLowerCase() === "video" ? "mp4" : "jpg");
      } catch {
        ext = ad.mediaType?.toLowerCase() === "video" ? "mp4" : "jpg";
      }
      const fileName = `voice_ad_${ad._id}.${ext}`;

      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      toast.success("Advertisement downloaded successfully!");
    } catch (err) {
      toast.error("Failed to download voice ad");
    } finally {
      setDownloadAdId(null);
    }
  };

  const handleConfirmUploads = (adId) => {
    setFormData((prev) => ({ ...prev, adId }));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({ adId: "", videoLink: "", platformName: "", adTiming: "" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "adTiming") {
      let cleanValue = value.replace(/[^\d:]/g, "");
      const prevValue = formData.adTiming;
      const isDeleting = value.length < prevValue.length;
      if (value === "") { setFormData((prev) => ({ ...prev, [name]: "" })); return; }
      if (isDeleting && cleanValue.includes(":") && cleanValue.length <= 3) { cleanValue = cleanValue.replace(":", ""); }
      if (!isDeleting && cleanValue.length === 2 && !cleanValue.includes(":")) { cleanValue = cleanValue + ":"; }
      if (cleanValue.length > 5) { cleanValue = cleanValue.substring(0, 5); }
      setFormData((prev) => ({ ...prev, [name]: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmitConfirmation = async (e) => {
    e.preventDefault();
    if (!formData.videoLink || !formData.platformName || !formData.adTiming || !formData.adId) {
      toast.error("Please fill all required fields");
      return;
    }
    setShowSubmitSpinner(true);
    try {
      const submitData = {
        adId: formData.adId,
        videoLink: formData.videoLink,
        platform: formData.platformName,
        duration: formData.adTiming
      };
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reporter/ryv/post/submit/proof`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        toast.success("Upload confirmation submitted successfully!");
        setTimeout(() => {
          setShowSubmitSpinner(false);
          window.location.reload();
        }, 1200);
      }
      handleCloseModal();
    } catch (error) {
      setShowSubmitSpinner(false);
      toast.error(error.response?.data?.message || "Failed to submit confirmation");
    }
  };

  if (loading) return (
    <div className="min-h-[400px] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 py-2 sm:py-6 px-2 sm:px-6">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Modern Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-6"
      >
        <div className="relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-700 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 text-white">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10"></div>

          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-1.5 bg-white/20 backdrop-blur-md rounded-lg shadow-inner">
                  <FaCheckCircle className="text-lg sm:text-xl text-emerald-200" />
                </div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight leading-none uppercase italic">
                  Active Assignments
                </h1>
              </div>
              <p className="text-emerald-50 text-[10px] sm:text-sm font-medium max-w-xl opacity-80">
                You have <span className="font-black text-white px-1.5 py-0.5 bg-white/10 rounded-md">{ads.length}</span> active tasks. Download the media and submit proof after broadcasting.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      <InfluenerRaiseVoiceTabs />

      {error && (
        <div className="max-w-7xl mx-auto bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-3">
          <FaTimes className="shrink-0" />
          <p className="font-medium text-sm">{error}</p>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {ads.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[...ads]
              .filter(Boolean)
              .reverse()
              .slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
              .map((ad, idx) => (
                <motion.div
                  key={ad._id || ad.id || idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-slate-100 shadow-md hover:shadow-xl transition-all duration-300 flex overflow-hidden h-36 sm:h-44 group border-l-4 border-l-emerald-500"
                >
                  {/* Compact Media - Left Side */}
                  <div className="relative w-28 sm:w-48 h-full bg-slate-900 shrink-0 overflow-hidden">
                    {ad.image ? (
                      <img
                        src={ad.image.startsWith("http") ? ad.image : `${process.env.REACT_APP_API_BASE_URL}/${ad.image.replace(/\\/g, "/")}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : ad.video ? (
                      <video
                        src={ad.video.startsWith("http") ? ad.video : `${process.env.REACT_APP_API_BASE_URL}/${ad.video.replace(/\\/g, "/")}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100">
                        <FaBullhorn className="text-slate-200 text-2xl" />
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2 flex gap-1">
                      <span className="px-1.5 py-0.5 rounded-md text-[7px] font-black uppercase text-white bg-emerald-500">
                        Accepted
                      </span>
                    </div>
                  </div>

                  {/* Dense Content - Right Side */}
                  <div className="p-3 sm:p-4 flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between items-start gap-2 mb-1">
                      <h3 className="text-slate-900 font-extrabold text-sm sm:text-lg leading-tight line-clamp-1 group-hover:text-emerald-600 transition-colors">
                        {ad.description || 'Verified Assignment'}
                      </h3>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-[9px] font-bold text-slate-400 uppercase">
                      <span className="flex items-center gap-1"><FaMapMarkerAlt className="text-emerald-500 shrink-0" /> {ad.city}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-slate-300 shrink-0"></span>
                      <span className="flex items-center gap-1"><FaHistory className="text-slate-300" /> {new Date(ad.updatedAt || ad.createdAt).toLocaleDateString('en-GB')}</span>
                    </div>

                    <p className="text-slate-500 text-[10px] sm:text-xs leading-tight mt-2 line-clamp-2 italic opacity-80 border-l-2 border-slate-100 pl-2">
                      "{ad.description || 'Review the briefing and begin the assignment.'}"
                    </p>

                    <div className="mt-auto flex items-center gap-2 pt-2 border-t border-slate-50">
                      <button
                        onClick={() => handleDownload(ad)}
                        disabled={downloadAdId === ad._id}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-slate-50 text-slate-700 hover:bg-emerald-600 hover:text-white transition-all font-black text-[9px] uppercase border border-slate-100"
                      >
                        <FaDownload size={10} />
                        {downloadAdId === ad._id ? 'Downloading...' : 'Get Ad'}
                      </button>
                      <button
                        onClick={() => handleConfirmUploads(ad._id || ad.id)}
                        className="flex-[1.5] flex items-center justify-center gap-1.5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-100 transition-all font-black text-[9px] uppercase"
                      >
                        <FaUpload size={10} />
                        Confirm Proof
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-[2rem] border border-slate-100 p-12 text-center"
          >
            <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FaCheckCircle className="text-slate-200 text-4xl" />
            </div>
            <h3 className="text-xl font-black text-slate-800 mb-2">No Active Assignments</h3>
            <p className="text-slate-500 max-w-xs mx-auto text-sm">You haven't accepted any voice leads yet.</p>
          </motion.div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center mt-10 mb-10">
            <div className="flex items-center gap-2 bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === 1 ? 'text-slate-200' : 'text-emerald-600 hover:bg-emerald-50 active:scale-90'}`}
              >
                ←
              </button>
              <div className="flex items-center px-4 gap-1">
                <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Page</span>
                <span className="w-8 h-8 flex items-center justify-center bg-emerald-600 text-white rounded-lg text-xs font-black">{currentPage}</span>
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest px-1">of</span>
                <span className="text-xs font-black text-slate-800 uppercase tracking-widest">{totalPages}</span>
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-xl transition-all ${currentPage === totalPages ? 'text-slate-200' : 'text-emerald-600 hover:bg-emerald-50 active:scale-90'}`}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-slate-100"
            >
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-5 text-white relative">
                <h3 className="text-xl font-black uppercase italic tracking-tight">Upload Proof</h3>
                <p className="text-blue-100 text-[10px] font-medium opacity-80">Submit your broadcast details below</p>
                <button
                  onClick={handleCloseModal}
                  className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors"
                >
                  <FaTimes size={14} />
                </button>
              </div>

              <div className="p-5">
                <form onSubmit={handleSubmitConfirmation} className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1.5 h-4 bg-blue-500 rounded-full"></div>
                      <label className="text-[11px] font-black uppercase text-slate-500 tracking-wider">Source Details</label>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <select
                        name="platformName"
                        value={formData.platformName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none font-medium"
                        required
                      >
                        <option value="">Select Platform</option>
                        {platforms.map((platform, index) => (
                          <option key={index} value={platform.name || platform}>{platform.name || platform}</option>
                        ))}
                      </select>
                      <input
                        type="url"
                        name="videoLink"
                        value={formData.videoLink}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none font-medium"
                        placeholder="Video Link (https://...)"
                        required
                      />
                    </div>
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-4 bg-amber-500 rounded-full"></div>
                      <label className="text-[11px] font-black uppercase text-slate-500 tracking-wider">Broadcast Timing</label>
                    </div>
                    <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
                      <input
                        type="text"
                        name="adTiming"
                        value={formData.adTiming}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-white border border-amber-200 rounded-xl text-sm font-bold text-amber-900"
                        placeholder="01:30 (MM:SS)"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 py-3 text-slate-500 bg-slate-100 rounded-2xl hover:bg-slate-200 transition-all font-black text-[10px] uppercase"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-[2] py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 shadow-lg font-black text-[10px] uppercase flex items-center justify-center gap-2"
                    >
                      <FaUpload size={12} />
                      Submit Confirmation
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSubmitSpinner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-md z-[200]"
          >
            <div className="bg-white p-6 rounded-3xl shadow-2xl flex flex-col items-center border border-slate-100">
              <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3"></div>
              <span className="text-xs font-black text-slate-800 uppercase tracking-widest">Processing...</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InfluenerRaiseAcceptedVoice;
